import { BirthdayValidatorAdapter } from '../../infra/adapters/birthday-validator-adapter'
import { DocumentValidatorAdapter } from '../../infra/adapters/document-validator-adapter'
import { EmailValidatorAdapter } from '../../infra/adapters/email-validator-adapter'
import { PhoneValidatorAdapter } from '../../infra/adapters/phone-validator-adapter'
import { ZipValidatorAdapter } from '../../infra/adapters/validate-zip-adapter'
import { type FieldValidation } from '../protocols/field-validation'
import { type ValidateBirthdayAdapter } from '../protocols/validate-birthday-adapter'
import { type ValidateDocumentAdapter } from '../protocols/validate-document-adapter'
import { type ValidateEmailAdapter } from '../protocols/validate-email-adapter'
import { type ValidatePhoneAdapter } from '../protocols/validate-phone-adapter'
import { type ValidateZipAdapter } from '../protocols/validate-zip-adapter'
import { CompareValidation } from '../validators/compare-validation'
import { ContainsLowercaseValidation } from '../validators/contains-lowercase-validation'
import { ContainsNumberValidation } from '../validators/contains-number-validation'
import { ContainsSpecialCharacterValidation } from '../validators/contains-special-character-validation'
import { ContainsUppercaseValidation } from '../validators/contains-uppercase-validation'
import { BirthdayValidation } from '../validators/birthday-validation'
import { DocumentValidation } from '../validators/document-validation'
import { EmailValidation } from '../validators/email-validation'
import { MinItemsValidation } from '../validators/min-items-validation'
import { MinLengthValidation } from '../validators/min-length-validation'
import { MinValueValidation } from '../validators/min-value-validation'
import { PhoneValidation } from '../validators/phone-validation'
import { RequiredFieldValidation } from '../validators/required-field-validation'
import { ZipValidation } from '../validators/zip-validation'

export class ValidationBuilder {
  private readonly fieldName: string
  private readonly validations: FieldValidation[]
  private readonly validateEmailAdapter: ValidateEmailAdapter
  private readonly validatePhoneAdapter: ValidatePhoneAdapter
  private readonly validateBirthdayAdapter: ValidateBirthdayAdapter
  private readonly validateDocumentAdapter: ValidateDocumentAdapter
  private readonly validateZipAdapter: ValidateZipAdapter

  private constructor (
    fieldName: string,
    validations: FieldValidation[],
    validateEmailAdapter: ValidateEmailAdapter,
    validatePhoneAdapter: ValidatePhoneAdapter,
    validateBirthdayAdapter: ValidateBirthdayAdapter,
    validateDocumentAdapter: ValidateDocumentAdapter,
    validateZipAdapter: ValidateZipAdapter) {
    this.fieldName = fieldName
    this.validations = validations
    this.validateEmailAdapter = validateEmailAdapter
    this.validatePhoneAdapter = validatePhoneAdapter
    this.validateBirthdayAdapter = validateBirthdayAdapter
    this.validateDocumentAdapter = validateDocumentAdapter
    this.validateZipAdapter = validateZipAdapter
  }

  static field (fieldName: string): ValidationBuilder {
    const validateEmailAdapter = new EmailValidatorAdapter()
    const validatePhoneAdapter = new PhoneValidatorAdapter()
    const validateBirthdayAdapter = new BirthdayValidatorAdapter()
    const validateDocumentAdapter = new DocumentValidatorAdapter()
    const validateZipAdapter = new ZipValidatorAdapter()
    return new ValidationBuilder(
      fieldName, [],
      validateEmailAdapter,
      validatePhoneAdapter,
      validateBirthdayAdapter,
      validateDocumentAdapter,
      validateZipAdapter
    )
  }

  required (): this {
    this.validations.push(new RequiredFieldValidation(this.fieldName))
    return this
  }

  email (): this {
    this.validations.push(new EmailValidation(this.fieldName, this.validateEmailAdapter))
    return this
  }

  phone (): this {
    this.validations.push(new PhoneValidation(this.fieldName, this.validatePhoneAdapter))
    return this
  }

  birthday (): this {
    this.validations.push(new BirthdayValidation(this.fieldName, this.validateBirthdayAdapter))
    return this
  }

  document (): this {
    this.validations.push(new DocumentValidation(this.fieldName, this.validateDocumentAdapter))
    return this
  }

  zip (): this {
    this.validations.push(new ZipValidation(this.fieldName, this.validateZipAdapter))
    return this
  }

  minLength (length: number): this {
    this.validations.push(new MinLengthValidation(this.fieldName, length))
    return this
  }

  lowercase (): this {
    this.validations.push(new ContainsLowercaseValidation(this.fieldName))
    return this
  }

  uppercase (): this {
    this.validations.push(new ContainsUppercaseValidation(this.fieldName))
    return this
  }

  compare (): this {
    this.validations.push(new CompareValidation(this.fieldName))
    return this
  }

  number (): this {
    this.validations.push(new ContainsNumberValidation(this.fieldName))
    return this
  }

  min (value: number): this {
    this.validations.push(new MinValueValidation(this.fieldName, value))
    return this
  }

  symbol (): this {
    this.validations.push(new ContainsSpecialCharacterValidation(this.fieldName))
    return this
  }

  minItems (): this {
    this.validations.push(new MinItemsValidation(this.fieldName))
    return this
  }

  build (): FieldValidation[] {
    return this.validations
  }
}
